/*
 * Copyright (c) 2025. BrightNight. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */

import { Stack, Tooltip, Typography } from '@mui/material';
import type { StandardSchemaV1Issue } from '@tanstack/react-form';
import { LoadingButton, type LoadingButtonProps } from '../buttons';
import { useFormContext } from './formContext';

interface Props extends Omit<LoadingButtonProps, 'onClick' | 'loading' | 'value' | 'startIcon'> {
  label?: string;
  loading?: boolean;
  startIcon?: React.ReactNode | ((disabled: boolean) => React.ReactNode);
  onSubmitMeta?: Record<string, unknown>;
  disableIfNotChanged?: boolean;
  showErrorsTooltip?: boolean;
}

const collectErrorMessages = (
  errorMaps: Array<Partial<Record<string, Array<StandardSchemaV1Issue>>> | undefined>,
): Array<string> => {
  const messages = new Map<string, string>();
  for (const errorMap of errorMaps) {
    for (const [field, issues] of Object.entries(errorMap ?? {})) {
      const message = issues?.[0]?.message;
      if (message && !messages.has(field)) {
        messages.set(field, message);
      }
    }
  }
  return [...new Set(messages.values())];
};

function SubmitButton({
  label,
  disabled,
  loading,
  startIcon,
  onSubmitMeta,
  disableIfNotChanged = true,
  showErrorsTooltip = false,
  ...props
}: Readonly<Props>) {
  const appForm = useFormContext();

  return (
    <appForm.Subscribe
      selector={(state) => [state.isSubmitting, state.isFormValid, state.isDefaultValue] as const}
    >
      {([isSubmitting, isFormValid, isDefaultValue]) => {
        const disableButton =
          disabled || !isFormValid || (disableIfNotChanged ? isDefaultValue : false);
        const button = (
          <LoadingButton
            {...props}
            loading={(isSubmitting || loading) ?? false}
            variant={props.variant ?? 'contained'}
            startIcon={typeof startIcon === 'function' ? startIcon(disableButton) : startIcon}
            onClick={() => appForm.handleSubmit(onSubmitMeta)}
            disabled={disableButton}
          >
            {label ?? props.children ?? 'Save'}
          </LoadingButton>
        );
        if (!showErrorsTooltip || !disableButton) {
          return button;
        }
        return (
          <appForm.Subscribe selector={(state) => state.errorMap}>
            {(errorMap) => {
              const messages = collectErrorMessages([
                errorMap?.onMount as Partial<Record<string, Array<StandardSchemaV1Issue>>>,
                errorMap?.onChange as Partial<Record<string, Array<StandardSchemaV1Issue>>>,
                errorMap?.onSubmit as Partial<Record<string, Array<StandardSchemaV1Issue>>>,
              ]);
              let title: React.ReactNode = '';
              if (!isFormValid) {
                title = (
                  <Stack spacing={0.25}>
                    {(messages.length > 0 ? messages : ['Form has invalid inputs']).map(
                      (message) => (
                        <Typography key={message} variant="caption">
                          {message}
                        </Typography>
                      ),
                    )}
                  </Stack>
                );
              } else if (disableIfNotChanged && isDefaultValue) {
                title = 'No changes to save';
              }
              return (
                <Tooltip title={title} arrow>
                  <span>{button}</span>
                </Tooltip>
              );
            }}
          </appForm.Subscribe>
        );
      }}
    </appForm.Subscribe>
  );
}

export default SubmitButton;
